import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  SeatSummaryVm, SeatAssignmentVm, AssignSeatDto,
  UnassignSeatDto, UpdateSeatDto, SetSeatAccessDto, SetIdentityAccessDto, PagedResponse, SeatRole, SeatStatus,
} from '../models';
import { enumName, enumIndex } from '../utils/enum.util';

@Injectable({ providedIn: 'root' })
export class SeatService {
  private http = inject(HttpClient);
  private base(tenantId: string) { return `${environment.apiBaseUrl}/tenants/${tenantId}/seats`; }

  private normalize(s: SeatAssignmentVm): SeatAssignmentVm {
    return { ...s, role: enumName(SeatRole, s.role), status: enumName(SeatStatus, s.status) };
  }

  getSummary(tenantId: string): Observable<SeatSummaryVm> {
    return this.http.get<SeatSummaryVm>(this.base(tenantId)).pipe(
      map((s) => ({ ...s, assignments: (s.assignments ?? []).map((a) => this.normalize(a)) })),
    );
  }

  list(tenantId: string, page = 1, pageSize = 20): Observable<PagedResponse<SeatAssignmentVm>> {
    const hp = new HttpParams().set('page', page.toString()).set('pageSize', pageSize.toString());
    return this.http.get<PagedResponse<SeatAssignmentVm>>(`${this.base(tenantId)}/list`, { params: hp }).pipe(
      map((res) => ({ ...res, items: res.items.map((a) => this.normalize(a)) })),
    );
  }

  assign(tenantId: string, dto: AssignSeatDto): Observable<SeatAssignmentVm> {
    const body = { ...dto, role: enumIndex(SeatRole, dto.role) };
    return this.http.post<SeatAssignmentVm>(`${this.base(tenantId)}/assign`, body).pipe(map((a) => this.normalize(a)));
  }

  unassign(tenantId: string, dto: UnassignSeatDto): Observable<void> {
    return this.http.post<void>(`${this.base(tenantId)}/unassign`, dto);
  }

  update(tenantId: string, seatId: string, dto: UpdateSeatDto): Observable<SeatAssignmentVm> {
    const body = { ...dto, role: dto.role == null ? undefined : enumIndex(SeatRole, dto.role) };
    return this.http.patch<SeatAssignmentVm>(`${this.base(tenantId)}/${seatId}`, body).pipe(map((a) => this.normalize(a)));
  }

  setAccess(tenantId: string, seatId: string, dto: SetSeatAccessDto): Observable<SeatAssignmentVm> {
    return this.http.put<SeatAssignmentVm>(`${this.base(tenantId)}/${seatId}/access`, dto).pipe(map((a) => this.normalize(a)));
  }

  // Drives the user's per-module access (User/Admin) into the tenant's Identity database.
  setIdentityAccess(tenantId: string, seatId: string, dto: SetIdentityAccessDto): Observable<{ updated: boolean }> {
    return this.http.put<{ updated: boolean }>(`${this.base(tenantId)}/${seatId}/identity-access`, dto);
  }
}
